'use client'

import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import ListBox from "@/components/ui/ListBox"
import DatePickerCustom from "@/components/ui/DatePickerCustom"
import NumberofTravelers from "@/components/ui/NumberofTravelers"
import { addDays } from 'date-fns'
import { useLanguageVariables } from "@/contexts/LanguageVariablesContext"
import { setTravelersPreferences } from "@/lib/cookies"
import { toLocalDateString } from "@/lib/date-utils"
import { ProductTypeConstants } from "@/constants/product-types"

export default function YachtSearchForm() {
    const { getVariable } = useLanguageVariables()
    const [checkInDate, setCheckInDate] = useState<Date | undefined>(undefined)
    const [checkOutDate, setCheckOutDate] = useState<Date | undefined>(undefined)
    const [travelers, setTravelers] = useState({
        adults: 1,
        children: 0,
        childAges: [] as number[]
    });

    const handleTravelersChange = useCallback((travelersData: { adults: number; children: number; childAges: number[] }) => {
        setTravelers(travelersData);
    }, []);

    const handleSubmit = () => {
        if (!checkInDate || !checkOutDate) {
            alert(`⚠️ ${getVariable('yacht_search_dates_required', 'Please select check-in and check-out dates!')}`)
            return
        }

        // Cookie'ye travelers bilgisini kaydet (oda bazlı yapı)
        setTravelersPreferences({
            product_type: ProductTypeConstants.DEFAULT, // Yacht için henüz destek yok, default olarak kaydediyoruz
            departure_date: toLocalDateString(checkInDate),
            rooms: [{
                adults: travelers.adults,
                children: travelers.children,
                childAges: travelers.childAges || [],
            }],
        });

        // TODO: Kategori sayfasına yönlendirme
        // Şimdilik alert göster
        alert(getVariable('yacht_search_submitted', 'Yacht search form submitted! Travelers and date information saved to cookie.'))
        
  
    }

    return (
        <motion.section
            key="panel-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{
                duration: 0.15,
                ease: "easeOut"
            }}
            className="rounded-xl bg-white/5 p-3"
        >
            <motion.div
                className="flex flex-row items-end gap-3"
                initial="hidden"
                animate="visible"
                variants={{
                    hidden: { opacity: 0 },
                    visible: {
                        opacity: 1,
                        transition: {
                            staggerChildren: 0.03,
                            delayChildren: 0
                        }
                    }
                }}
            >
                <ListBox
                    title={getVariable('yacht_search_location', 'Location')}
                    icon={
                        <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray">
                            <path fillRule="evenodd" clipRule="evenodd" d="M8.66675 0.916748C4.43257 0.916748 0.916748 4.43257 0.916748 8.66675C0.916748 13.7917 7.43257 20.3076 8.12091 20.9959C8.31466 21.1897 8.60091 21.2292 8.66675 21.2292C8.73258 21.2292 9.01883 21.1897 9.21258 20.9959C9.90091 20.3076 16.4167 13.7917 16.4167 8.66675C16.4167 4.43257 12.9009 0.916748 8.66675 0.916748ZM8.66675 19.2709C6.93466 17.5388 2.50008 12.1667 2.50008 8.66675C2.50008 5.30841 5.30841 2.50008 8.66675 2.50008C12.0251 2.50008 14.8334 5.30841 14.8334 8.66675C14.8334 12.1667 10.3988 17.5388 8.66675 19.2709Z"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M8.66675 5.45841C6.89425 5.45841 5.45841 6.89425 5.45841 8.66675C5.45841 10.4392 6.89425 11.8751 8.66675 11.8751C10.4392 11.8751 11.8751 10.4392 11.8751 8.66675C11.8751 6.89425 10.4392 5.45841 8.66675 5.45841ZM8.66675 10.2917C7.76883 10.2917 7.04175 9.56466 7.04175 8.66675C7.04175 7.76883 7.76883 7.04175 8.66675 7.04175C9.56466 7.04175 10.2917 7.76883 10.2917 8.66675C10.2917 9.56466 9.56466 10.2917 8.66675 10.2917Z"/>
                        </svg>
                    }
                    options={
                        [
                            {title: 'Bodrum', value: 'bodrum', id: 1, icon:null},
                            {title: 'Marmaris', value: 'marmaris', id: 2, icon:null},
                            {title: 'Fethiye', value: 'fethiye', id: 3, icon:null},
                            {title: 'Göcek', value: 'gocek', id: 4, icon:null},
                        ]
                    }
                />
                <motion.div className="w-full" variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}>
                    <DatePickerCustom
                        label={getVariable('yacht_search_check_in', 'Check-in')}
                        selected={checkInDate}
                        onSelect={(date) => {
                            setCheckInDate(date)
                            if (checkOutDate && date && checkOutDate <= date) {
                                setCheckOutDate(undefined)
                            }
                        }}
                        disabled={{ before: new Date() }}
                    />
                </motion.div>
                <motion.div className="w-full" variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}>
                    <DatePickerCustom
                        label={getVariable('yacht_search_check_out', 'Check-out')}
                        selected={checkOutDate}
                        onSelect={(date) => setCheckOutDate(date)}
                        disabled={{ before: checkInDate ? addDays(checkInDate, 1) : new Date() }}
                        isDisabled={!checkInDate}
                    />
                </motion.div>
                <NumberofTravelers 
                    title={getVariable('yacht_search_travelers', 'Number of Travelers')} 
                    onChange={handleTravelersChange}
                    icon={
                        <svg width="19" height="20" viewBox="0 0 19 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="fill-alsero-dark-gray">
                            <path d="M15.1811 7.77945C14.7379 9.41938 15.0146 11.6979 16.1374 12.9727C16.3996 13.2703 16.6426 13.5861 16.8643 13.9183C17.7646 15.2672 18.2809 16.8358 18.3576 18.4558C18.3678 18.6042 18.3483 18.7531 18.3002 18.8939C18.252 19.0346 18.1763 19.1643 18.0773 19.2754C17.9784 19.3865 17.8583 19.4767 17.724 19.5407C17.5898 19.6047 17.4441 19.6412 17.2955 19.6482C17.1469 19.6551 16.9984 19.6323 16.8588 19.5811C16.7191 19.5299 16.5911 19.4513 16.4823 19.3499C16.3734 19.2486 16.2859 19.1265 16.2249 18.9908C16.1638 18.8552 16.1305 18.7087 16.1268 18.56C16.0457 16.8394 15.305 15.216 14.0585 14.027C12.8121 12.838 11.1557 12.1747 9.43311 12.1747C7.71052 12.1747 6.05409 12.838 4.80767 14.027C3.56124 15.216 2.82057 16.8394 2.73938 18.56C2.71932 18.8513 2.58602 19.1231 2.36802 19.3173C2.15002 19.5115 1.86465 19.6126 1.57303 19.5989C1.28141 19.5853 1.00671 19.458 0.807769 19.2444C0.608827 19.0307 0.501452 18.7476 0.50864 18.4558C0.585226 16.836 1.10121 15.2675 2.00128 13.9186C2.22286 13.5865 2.46567 13.2708 2.72769 12.9732C3.85043 11.6981 4.12692 9.41954 3.68358 7.77945C3.36429 6.59825 3.4166 5.34736 3.83341 4.19695C4.25022 3.04653 5.01128 2.05243 6.01307 1.34986C7.01487 0.647302 8.20877 0.270386 9.43236 0.270386C10.656 0.270386 11.8499 0.647302 12.8517 1.34986C13.8534 2.05243 14.6145 3.04653 15.0313 4.19695C15.4481 5.34736 15.5004 6.59825 15.1811 7.77945ZM13.156 6.22244C13.156 5.23507 12.7638 4.28814 12.0656 3.58997C11.3674 2.89179 10.4205 2.49956 9.43311 2.49956C8.44574 2.49956 7.49881 2.89179 6.80064 3.58997C6.10247 4.28814 5.71024 5.23507 5.71024 6.22244C5.71024 7.2098 6.10247 8.15673 6.80064 8.8549C7.49881 9.55308 8.44574 9.94531 9.43311 9.94531C10.4205 9.94531 11.3674 9.55308 12.0656 8.8549C12.7638 8.15673 13.156 7.2098 13.156 6.22244Z"/>
                        </svg>
                    }
                />
                <motion.button
                    onClick={handleSubmit}
                    type="button"
                    className="bg-alsero-first text-lg font-medium rounded-lg text-nowrap text-white px-10 min-h-[60px] cursor-pointer hover:bg-alsero-dark-gray transition-colors duration-300"
                    variants={{hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 }}}
                >
                    {getVariable('yacht_search_button', 'Search')}
                </motion.button>
            </motion.div>
        </motion.section>
    )
}

